import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, fileUrl, apiErr } from "../lib/api";
import { Plus, Lock, Unlock, Trash2, Loader2, X, Sparkles, Apple } from "lucide-react";

function GenerateModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [fruit, setFruit] = useState("");
  const [personality, setPersonality] = useState("");
  const [color, setColor] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!name || !fruit || !personality) { toast.error("Name, fruit and personality are required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/cast", { name, fruit, personality, color });
      toast.success(`${data.name} the ${data.fruit} is ready & locked`);
      onCreated(data);
      setName(""); setFruit(""); setPersonality(""); setColor("");
      onClose();
    } catch (e) {
      toast.error(apiErr(e, "Character generation failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={loading ? undefined : onClose} />
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            className="glass relative z-10 w-full max-w-lg rounded-3xl p-7"
            data-testid="generate-character-modal"
          >
            <button className="absolute right-5 top-5 text-slate-400 hover:text-white" onClick={onClose} data-testid="close-character-modal">
              <X className="h-5 w-5" />
            </button>
            <h3 className="font-display text-xl font-semibold mb-1 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#FFD166]" /> Generate a fruit character
            </h3>
            <p className="text-sm text-slate-400 mb-5">A 3D Pixar-style character sheet is generated and locked for consistency.</p>
            <div className="space-y-3">
              <Field label="Character name" value={name} set={setName} placeholder="Sammy" testid="char-name-input" />
              <Field label="Fruit" value={fruit} set={setFruit} placeholder="strawberry" testid="char-fruit-input" />
              <Field label="Personality" value={personality} set={setPersonality} placeholder="shy and sweet" testid="char-personality-input" />
              <Field label="Color accent (optional)" value={color} set={setColor} placeholder="bright red" testid="char-color-input" />
            </div>
            <button
              onClick={submit} disabled={loading}
              className="mt-6 w-full candy-strawberry rounded-xl py-3 font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
              data-testid="submit-character-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? "Designing character…" : "Generate character sheet"}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, set, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-400 mb-1 block">{label}</label>
      <input
        data-testid={testid}
        value={value} onChange={(e) => set(e.target.value)} placeholder={placeholder}
        className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-[#FF3B60] transition-colors"
      />
    </div>
  );
}

export default function CastLibrary({ cast, refresh }) {
  const [open, setOpen] = useState(false);

  const toggleLock = async (c) => {
    try {
      const { data } = await api.post(`/cast/${c.id}/toggle-lock`);
      toast.success(data.locked ? "Character locked" : "Character unlocked");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (c) => {
    try { await api.delete(`/cast/${c.id}`); toast.success("Removed from cast"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Cast Library</h2>
          <p className="text-sm text-slate-400 mt-1">Your reusable fruit stars — generate once, binge a series.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="candy-strawberry rounded-xl px-5 py-2.5 font-semibold text-white flex items-center gap-2 hover:opacity-90 transition-opacity"
          data-testid="generate-character-btn"
        >
          <Plus className="h-4 w-4" /> New Character
        </button>
      </div>

      {cast.length === 0 ? (
        <div className="glass rounded-3xl p-14 text-center" data-testid="cast-empty">
          <div className="h-16 w-16 rounded-2xl candy-lime grid place-items-center mx-auto mb-4 animate-float">
            <Apple className="h-8 w-8 text-white" />
          </div>
          <p className="font-display text-lg">No characters yet</p>
          <p className="text-sm text-slate-400 mt-1">Generate your first fruit star to begin your love story.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {cast.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="glass rounded-2xl overflow-hidden group hover:border-[#FF3B60]/50 transition-colors"
              data-testid={`cast-card-${c.fruit}`}
            >
              <div className="vframe aspect-[3/4] border-0 rounded-none">
                <img src={fileUrl(c.image_path)} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-2 right-2">
                  <button onClick={() => toggleLock(c)} className="h-8 w-8 grid place-items-center rounded-lg bg-black/60 backdrop-blur hover:bg-black/80" data-testid={`lock-btn-${c.id}`}>
                    {c.locked ? <Lock className="h-4 w-4 text-[#2ECC71]" /> : <Unlock className="h-4 w-4 text-slate-300" />}
                  </button>
                </div>
              </div>
              <div className="p-3.5">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm truncate">{c.name}</h3>
                  <button onClick={() => remove(c)} className="text-slate-500 hover:text-[#FF7675]" data-testid={`delete-btn-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="text-xs text-[#FF8A00] capitalize mt-0.5">{c.fruit}</p>
                <p className="text-xs text-slate-400 mt-1.5 line-clamp-2">{c.personality}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <GenerateModal open={open} onClose={() => setOpen(false)} onCreated={() => refresh()} />
    </div>
  );
}
